import { useState, useEffect, type Dispatch, type SetStateAction } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { useAccountCreationContext } from '@/components/signup/account-creation/hooks/account-creation-context';
import { emailIsValid } from '@/components/signup/utils/email-is-valid';
import { fontSize } from '@/components/signup/utils/font-size';
import { wacToken } from '@/libraries/design/foundations';

import { InlineEmbeddedSignupInput } from './inline-embedded-signup-input';
import { InlineSeparator } from './inline-separator';
import { InlineSubmit } from './inline-submit';
import { SocialButton } from './social-button';

/** Atlaskit `media.above.md` — horizontal desktop layout at 1024px+ */
const DESKTOP_MQ = '(min-width: 64rem)';

type FormFieldsProps = {
	email: string;
	setEmail: Dispatch<SetStateAction<string>>;
	isValid: boolean | undefined;
	errorMessageText: string;
};

/**
 * Inline embedded signup form (TWCG-2414 / spark-jira-pt-embedded-signup).
 * Named to distinguish from the broader “embedded signup” pattern (any signup
 * outside /try/cloud/signup). Pending experiment results, inline variants may
 * consolidate into a single version.
 * Dual trees (no layout media queries) so Compiled can't half-apply desktop styles:
 * - stacked (&lt; md / tablet+mobile): email → Sign up → Continue with Google
 * - desktop (md+): grey outer pill, white nested field + or + Google
 * @see https://www.figma.com/design/KhAdeSJF2y30N2lm8qmRmi/Homepage-JIra-Signup?node-id=105-3759
 */
export const InlineEmbeddedSignup = () => {
	const intl = useIntl();
	const { isReadyForInvalid, setIsEmailValid, typeAheadEmail } = useAccountCreationContext();
	const [email, setEmail] = useState(typeAheadEmail);
	const [isValid, setValid] = useState<boolean>();
	const [isDesktop, setIsDesktop] = useState(() =>
		typeof window !== 'undefined' ? window.matchMedia(DESKTOP_MQ).matches : false,
	);

	const errorMessageText = intl.formatMessage({
		id: 'BxpUiSignup.SignupForm.invalidEmail',
		defaultMessage: 'Please enter a valid email address.',
	});

	const labelText = intl.formatMessage({
		id: 'BxpUiSignup.SignupForm.enterWorkEmail',
		defaultMessage: 'Enter work email',
	});

	useEffect(() => {
		const mediaQuery = window.matchMedia(DESKTOP_MQ);
		const onChange = () => setIsDesktop(mediaQuery.matches);
		onChange();
		mediaQuery.addEventListener('change', onChange);
		return () => mediaQuery.removeEventListener('change', onChange);
	}, []);

	useEffect(() => {
		if (!email.trim()) {
			setValid(undefined);
			setIsEmailValid(false);
			return;
		}
		const valid = emailIsValid(email);
		if (!valid && !isReadyForInvalid) {
			setValid(undefined);
			return;
		}
		setValid(valid);
		setIsEmailValid(valid);
	}, [email, setIsEmailValid, isReadyForInvalid]);

	const fields: FormFieldsProps = {
		email,
		setEmail,
		isValid,
		errorMessageText,
	};

	return (
		<div css={containerCss} data-testid="inline-embedded-signup">
			<label css={srOnlyCss} htmlFor="inline_embedded_signup_email_input">
				{labelText}
			</label>
			{isDesktop ? (
				<>
					<DesktopForm {...fields} />
					{isValid === false ? (
						<div css={errorCss} role="alert">
							{errorMessageText}
						</div>
					) : null}
				</>
			) : (
				<StackedForm {...fields} />
			)}
		</div>
	);
};

const DesktopForm = (props: FormFieldsProps) => (
	<div css={desktopLayoutCss}>
		<div
			css={[
				desktopFieldCss,
				props.isValid === undefined && desktopFieldFocusCss,
				props.isValid === false && desktopFieldInvalidCss,
				props.isValid === true && desktopFieldValidCss,
			]}
		>
			<div css={[fieldInputChromeCss, desktopInputCss]}>
				<InlineEmbeddedSignupInput {...props} />
			</div>
			<div css={desktopSubmitCss}>
				<InlineSubmit />
			</div>
		</div>
		<div css={[separatorCss, desktopSeparatorCss]}>
			<InlineSeparator />
		</div>
		<div css={[socialButtonCss, desktopSocialCss]}>
			<SocialButton />
		</div>
	</div>
);

const StackedForm = (props: FormFieldsProps) => (
	<div css={stackedLayoutCss}>
		{/* Error under email field, not under Google */}
		<div css={stackedFieldGroupCss}>
			<div
				css={[
					stackedFieldCss,
					props.isValid === undefined && stackedFieldFocusCss,
					props.isValid === false && stackedFieldInvalidCss,
					props.isValid === true && stackedFieldValidCss,
				]}
			>
				<div css={[fieldInputChromeCss, stackedInputCss]}>
					<InlineEmbeddedSignupInput {...props} />
				</div>
			</div>
			{props.isValid === false ? (
				<div css={stackedErrorCss} role="alert">
					{props.errorMessageText}
				</div>
			) : null}
		</div>
		<div css={stackedSubmitCss}>
			<InlineSubmit />
		</div>
		<div css={[socialButtonCss, stackedSocialCss]}>
			<SocialButton label="Continue with Google" />
		</div>
	</div>
);

const containerCss = css({
	width: '100%',
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.050'),
});

const srOnlyCss = css({
	border: 0,
	clip: 'rect(0 0 0 0)',
	height: '1px',
	margin: '-1px',
	overflow: 'hidden',
	padding: 0,
	position: 'absolute',
	width: '1px',
	whiteSpace: 'nowrap',
});

/** Desktop: min 680 / max 905; height = field 53 + outer pad 8×2 */
const desktopLayoutCss = css({
	alignSelf: 'flex-start',
	width: 'max(680px, max-content)',
	maxWidth: '905px',
	height: '69px',
	display: 'flex',
	alignItems: 'center',
	gap: '10px',
	backgroundColor: wacToken('color.neutral.light200'),
	borderRadius: 100,
	boxSizing: 'border-box',
	padding: token('space.100'),
});

/** Stacked (tablet/mobile): full width of parent */
const stackedLayoutCss = css({
	width: '100%',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'stretch',
	gap: token('space.150'),
});

const stackedFieldGroupCss = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'stretch',
	gap: token('space.050'),
	width: '100%',
});

const desktopFieldCss = css({
	display: 'flex',
	flex: '0 0 auto',
	alignItems: 'center',
	gap: token('space.100'),
	width: 'max-content',
	minWidth: '560px',
	// 41px Sign up + 4px pad × 2 + 2px border × 2
	height: '53px',
	backgroundColor: wacToken('color.neutral.light0'),
	border: `2px solid ${wacToken('color.neutral.light200')}`,
	boxShadow: `inset 0 0 0 1px ${wacToken('color.neutral.light400')}`,
	borderRadius: 50,
	boxSizing: 'border-box',
	// No overflow:hidden — Sign up focus outline must cover the field border
	paddingBlock: '4px',
	paddingInlineEnd: '4px',
	paddingInlineStart: token('space.200'),
});

const stackedFieldCss = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'stretch',
	width: '100%',
});

const desktopFieldFocusCss = css({
	['&:has(input:focus)']: {
		borderColor: token('color.border.focused'),
		boxShadow: 'none',
	},
});

const stackedFieldFocusCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['& > div:first-of-type:focus-within']: {
		borderColor: token('color.border.focused'),
		boxShadow: 'none',
	},
});

const desktopFieldInvalidCss = css({
	borderColor: token('color.border.danger'),
	boxShadow: 'none',
});

const stackedFieldInvalidCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['& > div:first-of-type']: {
		borderColor: token('color.border.danger'),
		boxShadow: 'none',
	},
});

const desktopFieldValidCss = css({
	borderColor: token('color.border.success'),
	boxShadow: 'none',
});

const stackedFieldValidCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['& > div:first-of-type']: {
		borderColor: token('color.border.success'),
		boxShadow: 'none',
	},
});

/** Kill Atlaskit TextField chrome — field wrappers own the ring */
const fieldInputChromeCss = css({
	boxSizing: 'border-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['& > div, & > div:focus-within, & > div:focus, & div[data-ds--text-field--container]']: {
		backgroundColor: 'transparent !important',
		border: 'none !important',
		borderRadius: '0 !important',
		boxShadow: 'none !important',
		height: '100% !important',
		width: '100%',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input, input:focus, input:focus-visible']: {
		fontSize: '16px !important',
		fontWeight: 400,
		letterSpacing: '-0.16px',
		paddingInlineStart: '0 !important',
		paddingInlineEnd: '0 !important',
		boxShadow: 'none !important',
		outline: 'none !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input::placeholder']: {
		color: `${wacToken('color.primary.black')} !important`,
		fontSize: '16px !important',
	},
});

const desktopInputCss = css({
	// ~410 fills min 560 with EN Sign up; grow only, never shrink below 305
	flex: '1 0 410px',
	height: '100%',
	minWidth: '305px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['& > div']: {
		minWidth: 0,
		maxWidth: '100%',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input']: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});

const stackedInputCss = css({
	width: '100%',
	height: '46px',
	backgroundColor: wacToken('color.neutral.light0'),
	border: `2px solid ${wacToken('color.neutral.light0')}`,
	boxShadow: `inset 0 0 0 1px ${wacToken('color.neutral.light400')}`,
	borderRadius: 50,
	boxSizing: 'border-box',
	paddingInlineStart: token('space.200'),
	paddingInlineEnd: token('space.200'),
});

/** Nested desktop: white fill through pad+border; outline covers field edge */
const desktopSubmitFocusCss = {
	outline: `2px solid ${token('color.border.focused')} !important`,
	outlineOffset: '4px !important',
	boxShadow: `0 0 0 6px ${wacToken('color.neutral.light0')} !important`,
} as const;

/** Stacked: standard ADS focus (button is not nested in the field) */
const stackedSubmitFocusCss = {
	outline: `2px solid ${token('color.border.focused')} !important`,
	outlineOffset: '2px !important',
	boxShadow: `0 0 0 2px ${wacToken('color.neutral.light0')} !important`,
} as const;

const desktopSubmitCss = css({
	flexShrink: 0,
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button']: {
		height: '41px !important',
		minHeight: '41px !important',
		margin: '0 !important',
		maxWidth: 'none !important',
		minWidth: 'auto !important',
		width: 'auto !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button:focus-visible']: desktopSubmitFocusCss,
});

const stackedSubmitCss = css({
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button']: {
		height: '41px !important',
		minHeight: '41px !important',
		margin: '0 !important',
		maxWidth: '100% !important',
		width: '100% !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button:focus-visible']: stackedSubmitFocusCss,
});

const separatorCss = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['span']: {
		fontSize: '16px',
		letterSpacing: '-0.16px',
		lineHeight: 1.56,
	},
});

const desktopSeparatorCss = css({
	flexShrink: 0,
	whiteSpace: 'nowrap',
});

const socialButtonCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button']: {
		backgroundColor: `${wacToken('color.neutral.light0')} !important`,
		border: `1px solid ${wacToken('color.neutral.light400')} !important`,
		borderRadius: '40px !important',
		color: `${wacToken('color.primary.black')} !important`,
		fontSize: '16px !important',
		fontWeight: '400 !important',
		gap: `${token('space.100')} !important`,
		letterSpacing: '-0.16px',
		margin: '0 !important',
		maxWidth: 'none !important',
		paddingBlock: '11px !important',
		paddingInline: '24px !important',
		width: 'auto !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button:hover']: {
		backgroundColor: `${wacToken('color.neutral.light200')} !important`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button:focus, button:focus-visible']: {
		backgroundColor: `${wacToken('color.neutral.light0')} !important`,
		borderColor: `${token('color.border.focused')} !important`,
		boxShadow: `inset 0 0 0 1px ${token('color.border.focused')} !important`,
		outline: 'none !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['svg']: {
		height: '20px',
		minHeight: '20px',
		minWidth: '20px',
		width: '20px',
	},
});

const desktopSocialCss = css({
	width: 'auto',
	flexShrink: 0,
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button']: {
		height: '53px !important',
		minHeight: '53px !important',
	},
});

const stackedSocialCss = css({
	width: '100%',
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button']: {
		height: '41px !important',
		minHeight: '41px !important',
		width: '100% !important',
		maxWidth: '100% !important',
	},
});

const errorTextCss = {
	color: token('color.text.danger'),
	fontSize: fontSize.xs,
	lineHeight: 1.5,
} as const;

const errorCss = css({
	...errorTextCss,
	marginInlineStart: '24px',
	marginInlineEnd: '24px',
});

const stackedErrorCss = css({
	...errorTextCss,
	marginInlineStart: token('space.200'),
});
